import React, { useEffect, useState, useContext, useMemo } from "react";
import { useMsal } from "@azure/msal-react";
import { UIContext } from "../../../providers/UIProvider";
import { useData } from "../../../providers/DataProvider";
import httpClient from "../../../utils/httpClient";
import { CompanyProps, RegionProps } from "../../../providers/types";
import {
  isApprovedJob,
  getMaxCMs,
  getApprovedColumns,
  getPendingColumns,
  getApprovedCellValue,
  getPendingCellValue,
  formatCurrency,
  formatDate
} from "../../../components/PayrollManagementBase/TableHelpers";

type CMData = {
  employeeNumber: string;
  cm: string;
  bonusPercent: number;
  bonusAmount: number;
  bonusType: string;
  paidDate?: string;
  initialCM?: string;
  initialBonusPercent?: number;
  initialBonusAmount?: number;
  initialBonusType?: string;
};

type JobData = {
  jobId: number;
  areaID: number;
  areaName: string;
  projectNumber: string;
  subdivision: string;
  stage5Bonus: number;
  closingBonus: number;
  jobNumber: string;
  salesPrice: number;
  jobAddress: string;
  closeDate: string;
  stageCode: string;
  stage5Date: string;
  bonusStatusId: number;
  cMs: CMData[];
  notes: string;
  company?: string;
  companyID: number;
  regionID: number;
  regionName?: string;
  // Pending job fields
  year?: number;
  month?: number;
  statusName?: string;
  createdOn?: string;
  createdBy?: string;
  pmReviewer?: string;
  pmReviewedOn?: string;
  opsReviewer?: string;
  opsReviewedOn?: string;
  updatedOn?: string;
  updatedBy?: string;
  spmReviewer?: string;
  spmReviewedOn?: string;
  dmReviewer?: string;
  dmReviewedOn?: string;
  payrollProcessor?: string;
  payrollProcessedOn?: string;
  projectedCloseDate?: string;
};

type FilterOptions = {
  companies: CompanyProps[];
  regions: RegionProps[];
};

type DateFilterType = 'none' | 'month' | 'dateRange' | 'before' | 'after';

type PayrollSearchFilters = {
  companyId: number;
  regionId: number | "All" | '';
  statusId: number | 'Pre-Approval' | 'Approved' | '';
  bonusTypeId: number | '';
  dateFilterType: DateFilterType;
  month: string;
  startDate: string;
  endDate: string;
  beforeDate: string;
  afterDate: string;
};

// Determine column set based on filtered rows and API response type
const useTableColumns = (rows: JobData[], bonusType: string, isApprovedFromApi: boolean) => {
  return useMemo(() => {
    if (rows.length === 0) {
      return { columns: isApprovedFromApi ? getApprovedColumns(bonusType) : getPendingColumns(bonusType), isApproved: isApprovedFromApi };
    }
    
    // Use the API response type to determine columns
    if (isApprovedFromApi) {
      return { columns: getApprovedColumns(bonusType), isApproved: true };
    } else {
      return { columns: getPendingColumns(bonusType), isApproved: false };
    }
  }, [rows, bonusType, isApprovedFromApi]);
};

export default function StatusOverview() {
  const { role, bonusType } = useData();
  const { accounts } = useMsal();
  const [state, dispatch] = useContext<any>(UIContext);
  const [loading, setLoading] = useState<boolean>(false);
  const [rows, setRows] = useState<JobData[]>([]);
  const [filteredRows, setFilteredRows] = useState<JobData[]>([]);
  const [isApprovedData, setIsApprovedData] = useState<boolean>(true);
  
  // Get dynamic columns based on filtered rows and API response type
  const { columns, isApproved } = useTableColumns(filteredRows, bonusType, isApprovedData);
  
  // Filter states
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    companies: [],
    regions: []
  });
  const [filters, setFilters] = useState<PayrollSearchFilters>(() => {
    // Preselect bonus type based on header bar selection
    const defaultBonusTypeId = bonusType === "Stage5Bonus" ? 1 : bonusType === "ClosingBonus" ? 2 : '';
    return {
      companyId: 0,
      regionId: 0,
      statusId: '',
      bonusTypeId: defaultBonusTypeId,
      dateFilterType: 'none',
      month: '',
      startDate: '',
      endDate: '',
      beforeDate: '',
      afterDate: ''
    };
  });

  // Load filter options
  const loadFilterOptions = async () => {
    try {
      const [companiesRes, regionsRes] = await Promise.all([
        httpClient.get<CompanyProps[]>("/api/cba/companies"),
        httpClient.get<RegionProps[]>("/api/cba/regions")
      ]);
      
      setFilterOptions({
        companies: companiesRes?.data || [],
        regions: regionsRes?.data || []
      });
    } catch (error) {
      console.error("Failed to load filter options:", error);
      setFilterOptions({
        companies: [],
        regions: []
      });
    }
  };

  // Apply filters to rows
  const applyFilters = () => {
    let filtered = [...rows];
    console.log("Applying filters. Total rows:", filtered.length);
    // Don't filter out rows without CMs - show all jobs for Status Overview
    // If a job has no CMs, we'll display it with empty CM fields

    // Filter by company
    if (filters.companyId) {
      const selectedCompany = filterOptions.companies.find(c => c.companyId === filters.companyId);
      if (selectedCompany) {
        filtered = filtered.filter(row => 
          row.companyID === selectedCompany.companyId
        );
      }
    }

    // Filter by region
    if (filters.regionId) {
      if (filters.regionId === 'All') {
        // 'All' means no filtering by region
      } else {
        const selectedRegion = filterOptions.regions.find(r => r.regionId === filters.regionId);
        if (selectedRegion) {
          filtered = filtered.filter(row => 
            row.regionID === selectedRegion.regionId
          );
        }
      }
    }

    // Note: Status filtering is handled server-side by the API
    // The API already returns only records matching the selected status
    // So we don't need to filter by status on the client side
    // This avoids issues with BonusStatusId being JsonIgnore in the API response

    // Filter by bonus type
    if (filters.bonusTypeId !== '') {
      const bonusTypeName = filters.bonusTypeId === 1 ? 'Stage5Bonus' : 'ClosingBonus';
      // Filter CMs within each row to only show matching bonus types
      filtered = filtered.map(row => ({
        ...row,
        cMs: (row.cMs || []).filter(cm => cm.bonusType === bonusTypeName || cm.bonusType === 'Stage 5 Bonus' || cm.bonusType === 'Closing Bonus')
      }));
      // Only filter out rows if they have CMs but none match the bonus type
      // Keep rows with no CMs (they'll show as empty)
      filtered = filtered.filter(row => 
        !row.cMs || row.cMs.length === 0 || row.cMs.length > 0
      );
    }

    // Filter by date
    const bonusTypeId = filters.bonusTypeId !== '' ? filters.bonusTypeId : (bonusType === "Stage5Bonus" ? 1 : 2);
    const dateToUse = bonusTypeId === 1 ? 'stage5Date' : 'closeDate'; // Stage5Bonus uses stage5Date, ClosingBonus uses closeDate
    
    if (filters.dateFilterType === 'month' && filters.month) {
      const [year, month] = filters.month.split('-');
      filtered = filtered.filter(row => {
        if (row[dateToUse]) {
          const rowDate = new Date(row[dateToUse]);
          return rowDate.getFullYear() === parseInt(year) && 
                 rowDate.getMonth() === parseInt(month) - 1;
        }
        return false;
      });
    } else if (filters.dateFilterType === 'dateRange' && filters.startDate && filters.endDate) {
      const start = new Date(filters.startDate);
      const end = new Date(filters.endDate);
      filtered = filtered.filter(row => {
        // Use stage5Date for Stage5Bonus, closeDate for ClosingBonus
        if (!row[dateToUse]) return false;
        const rowDate = new Date(row[dateToUse]);
        return rowDate >= start && rowDate <= end;
      });
    } else if (filters.dateFilterType === 'before' && filters.beforeDate) {
      const before = new Date(filters.beforeDate);
      filtered = filtered.filter(row => {
        // Use stage5Date for Stage5Bonus, closeDate for ClosingBonus
        if (!row[dateToUse]) return false;
        const rowDate = new Date(row[dateToUse]);
        return rowDate < before;
      });
    } else if (filters.dateFilterType === 'after' && filters.afterDate) {
      const after = new Date(filters.afterDate);
      filtered = filtered.filter(row => {
        // Use stage5Date for Stage5Bonus, closeDate for ClosingBonus
        if (!row[dateToUse]) return false;
        const rowDate = new Date(row[dateToUse]);
        return rowDate > after;
      });
    }

    // Sort by Company, RegionName, Project Number, and then Employee Number
    filtered.sort((a, b) => {
      // Sort by Company
      const companyA = (a.company || '').toLowerCase();
      const companyB = (b.company || '').toLowerCase();
      if (companyA !== companyB) {
        return companyA.localeCompare(companyB);
      }

      // Sort by Region Name
      const regionA = (a.regionName || '').toLowerCase();
      const regionB = (b.regionName || '').toLowerCase();
      if (regionA !== regionB) {
        return regionA.localeCompare(regionB);
      }

      // Sort by Project Number
      const projectA = (a.projectNumber || '').toLowerCase();
      const projectB = (b.projectNumber || '').toLowerCase();
      if (projectA !== projectB) {
        return projectA.localeCompare(projectB);
      }

      return 0;
    });

    // Sort CMs within each job by Employee Number
    filtered.forEach(row => {
      if (row.cMs && row.cMs.length > 1) {
        row.cMs.sort((a, b) => {
          const empA = (a.employeeNumber || '').toLowerCase();
          const empB = (b.employeeNumber || '').toLowerCase();
          return empA.localeCompare(empB);
        });
      }
    });

    setFilteredRows(filtered);
  };

  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, rows, filterOptions, bonusType]);

  const handleFilterChange = (field: keyof PayrollSearchFilters, value: string) => {
    let parsedValue: any = value;
  
    // Parse numeric fields
    if (field === 'companyId') {
      parsedValue = value === '' ? 0 : parseInt(value, 10);
    } else if (field === 'regionId') {
      if (value === '' || value === 'All') {
        parsedValue = value === '' ? 0 : 'All';
      } else {
        parsedValue = parseInt(value, 10);
      }
    } else if (field === 'statusId') {
      // Keep as string if it's 'Pre-Approval' or 'Approved', otherwise parse as number
      if (value === '' || value === 'Pre-Approval' || value === 'Approved') {
        parsedValue = value;
      } else {
        parsedValue = parseInt(value, 10);
      }
    } else if (field === 'bonusTypeId') {
      parsedValue = value === '' ? '' : parseInt(value, 10);
    }
  
    setFilters(prev => ({ ...prev, [field]: parsedValue }));
  };

  const clearFilters = () => {
    // Reset to default bonus type from header bar
    const defaultBonusTypeId = bonusType === "Stage5Bonus" ? 1 : bonusType === "ClosingBonus" ? 2 : '';
    setFilters({
      companyId: 0,
      regionId: 0,
      statusId: 'Approved', // Default to 'Approved' to show all approved jobs on initial load
      bonusTypeId: defaultBonusTypeId,
      dateFilterType: 'none',
      month: '',
      startDate: '',
      endDate: '',
      beforeDate: '',
      afterDate: ''
    });
  };

  const loadTable = async () => {
    setLoading(true);
    try {
      // Build query parameters based on filters
      const params = new URLSearchParams();
      
      // Add bonusTypeId from filter (or use header bar default)
      const bonusTypeIdToUse = filters.bonusTypeId !== '' 
        ? filters.bonusTypeId 
        : (bonusType === "Stage5Bonus" ? 1 : 2);
      params.append('bonusTypeId', bonusTypeIdToUse.toString());
      
      // Add statusId if selected (can be "Pre-Approval", "Approved", or a number)
      if (filters.statusId !== '') {
        params.append('statusId', filters.statusId.toString());
      }

      const res = await httpClient.get<{
        isApproved: boolean;
        approvedJobs?: any[];
        pendingJobs?: any[];
      }>(
        `/api/cba/status-overview?${params.toString()}`,
        {
          headers: {
            roleType: role,
            BonusTypeId: bonusTypeIdToUse.toString(),
          },
        }
      );

      if (res && res.data) {
        console.log("API Response:", res.data);
        console.log("Is Approved:", res.data.isApproved);
        console.log("Approved Jobs:", res.data.approvedJobs?.length || 0);
        console.log("Pending Jobs:", res.data.pendingJobs?.length || 0);
        
        // Determine which data to use
        const sourceData = res.data.isApproved 
          ? (res.data.approvedJobs || [])
          : (res.data.pendingJobs || []);
        
        console.log("Source data length:", sourceData.length);
        console.log("First record:", sourceData[0]);
        console.log("First record keys:", Object.keys(sourceData[0] || {}));
        console.log("Current status filter:", filters.statusId);
        
        // Normalize property names based on response type
        const normalizedData = sourceData.map((row: any) => {
          // For approved jobs, normalize CMs array
          // For pending jobs, CMs will be empty
          const cMs = (row.cMs || row.CMs || []).map((cm: any) => ({
            employeeNumber: cm.employeeNumber || cm.EmployeeNumber || '',
            cm: cm.cm || cm.CM || '',
            bonusPercent: cm.bonusPercent || cm.BonusPercent || 0,
            bonusAmount: cm.bonusAmount || cm.BonusAmount || 0,
            bonusType: cm.bonusType || cm.BonusType || '',
            paidDate: cm.paidDate || cm.PaidDate || '',
            initialCM: cm.initialCM || cm.InitialCM || '',
            initialBonusPercent: cm.initialBonusPercent || cm.InitialBonusPercent || 0,
            initialBonusAmount: cm.initialBonusAmount || cm.InitialBonusAmount || 0,
            initialBonusType: cm.initialBonusType || cm.InitialBonusType || ''
          }));
          
          // Extract bonusStatusId
          const bonusStatusId = row.bonusStatusId || row.BonusStatusId || 0;
          
          // Common fields for both approved and pending
          const baseData: any = {
            jobId: row.jobId || row.JobId || 0,
            bonusStatusId: bonusStatusId,
            cMs: cMs,
            subdivision: row.subdivision || row.Subdivision || '',
            jobAddress: row.jobAddress || row.JobAddress || '',
            jobNumber: row.jobNumber || row.JobNumber || row.projectAndJobNumber || row.ProjectAndJobNumber || ''
          };
          
          // Approved job specific fields
          if (res.data.isApproved) {
            return {
              ...baseData,
              company: row.company || row.Company || '',
              companyID: row.companyID || row.CompanyID || 0,
              regionID: row.regionID || row.RegionID || 0,
              regionName: row.regionName || row.RegionName || '',
              areaName: row.areaName || row.AreaName || '',
              stage5Bonus: row.stage5Bonus || row.Stage5Bonus || 0,
              closingBonus: row.closingBonus || row.ClosingBonus || 0,
              salesPrice: row.salesPrice || row.SalesPrice || 0,
              closeDate: row.closeDate || row.CloseDate || '',
              stageCode: row.stageCode || row.StageCode || '',
              stage5Date: row.stage5Date || row.Stage5Date || '',
              projectedCloseDate: row.projectedCloseDate || row.ProjectedCloseDate || '',
              notes: row.notes || row.Notes || '',
              opsReviewedOn: row.opsReviewedOn || row.OpsReviewedOn || ''
            };
          } else {
            // Pending job specific fields
            // Convert Year and Month to numbers if they exist
            const yearValue = row.year !== undefined && row.year !== null ? (typeof row.year === 'number' ? row.year : parseInt(row.year, 10)) : 
                             (row.Year !== undefined && row.Year !== null ? (typeof row.Year === 'number' ? row.Year : parseInt(row.Year, 10)) : undefined);
            const monthValue = row.month !== undefined && row.month !== null ? (typeof row.month === 'number' ? row.month : parseInt(row.month, 10)) : 
                              (row.Month !== undefined && row.Month !== null ? (typeof row.Month === 'number' ? row.Month : parseInt(row.Month, 10)) : undefined);
            
            return {
              ...baseData,
              company: row.company || row.Company || '',
              companyID: row.companyID || row.CompanyID || 0,
              regionID: row.regionID || row.RegionID || 0,
              regionName: row.regionName || row.RegionName || '',
              areaID: row.areaID || row.AreaID || 0,
              areaName: row.areaName || row.AreaName || '',
              stage5Date: row.stage5Date || row.Stage5Date || '',
              closeDate: row.closeDate || row.CloseDate || '',
              year: yearValue,
              month: monthValue,
              statusName: row.statusName || row.StatusName || '',
              createdOn: row.createdOn || row.CreatedOn || '',
              createdBy: row.createdBy || row.CreatedBy || '',
              pmReviewer: row.pmReviewer || row.PMReviewer || '',
              pmReviewedOn: row.pmReviewedOn || row.PMReviewedOn || '',
              opsReviewer: row.opsReviewer || row.OpsReviewer || '',
              opsReviewedOn: row.opsReviewedOn || row.OpsReviewedOn || '',
              updatedOn: row.updatedOn || row.UpdatedOn || '',
              updatedBy: row.updatedBy || row.UpdatedBy || '',
              spmReviewer: row.spmReviewer || row.SPMReviewer || '',
              spmReviewedOn: row.spmReviewedOn || row.SPMReviewedOn || '',
              dmReviewer: row.dmReviewer || row.DMReviewer || '',
              dmReviewedOn: row.dmReviewedOn || row.DMReviewedOn || '',
              payrollProcessor: row.payrollProcessor || row.PayrollProcessor || '',
              payrollProcessedOn: row.payrollProcessedOn || row.PayrollProcessedOn || ''
            };
          }
        });
        console.log("Normalized data:", normalizedData);
        console.log("Sample bonusStatusIds:", normalizedData.slice(0, 5).map((r: any) => ({ jobId: r.jobId, bonusStatusId: r.bonusStatusId })));
        console.log("Records with CMs:", normalizedData.filter((r: any) => r.cMs && r.cMs.length > 0).length);
        
        // Store the isApproved flag from API response
        setIsApprovedData(res.data.isApproved);
        setRows(normalizedData);
      }
    } catch (error) {
      console.error("Failed to load table data:", error);
      dispatch({
        type: "Snackbar",
        payload: {
          show: true,
          message: "Failed to load status overview data. Please try again.",
          severity: "error",
        },
      });
    } finally {
      setLoading(false);
    }
  };

  // Load filter options on mount (companies and regions)
  useEffect(() => {
    loadFilterOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load table data only when status is selected (not on initial load)
  useEffect(() => {
    // Only load data if statusId is selected (not empty)
    if (filters.statusId !== '') {
      loadTable();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.bonusTypeId, filters.statusId]);

  // Update bonus type filter when header bar bonus type changes
  useEffect(() => {
    const defaultBonusTypeId = bonusType === "Stage5Bonus" ? 1 : bonusType === "ClosingBonus" ? 2 : '';
    setFilters(prev => ({
      ...prev,
      statusId: 'Approved', // Default to 'Approved' to show all approved jobs on initial load
      bonusTypeId: defaultBonusTypeId
    }));
  }, [bonusType]);

  return (
    <main className="flex-grow p-4">
      <div className="bg-white shadow-xl rounded-lg overflow-hidden">
        <div className="p-4 bg-gray-50 border-b border-gray-200">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center align-middle">
              <h2 className="text-xl font-semibold">Status Overview</h2>
            </div>
          </div>

          {/* Filter Panel */}
          <div className="mt-4 p-4 bg-white rounded-md border border-gray-300">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Company Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company
                  </label>
                  <select
                    value={filters.companyId}
                    onChange={(e) => handleFilterChange('companyId', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">All Companies</option>
                    {filterOptions.companies.map(company => (
                      <option key={company.companyId} value={company.companyId.toString()}>
                        {company.companyName}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Region Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Region
                  </label>
                  <select
                    value={filters.regionId}
                    onChange={(e) => handleFilterChange('regionId', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">All Regions</option>
                    {filterOptions.regions.map(region => (
                      <option 
                        key={region.regionId} 
                        value={region.regionId === -1 ? "All" : region.regionId.toString()}
                      >
                        {region.regionName}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Status Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={filters.statusId}
                    onChange={(e) => handleFilterChange('statusId', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="" disabled>Select Status</option>
                    <optgroup label="Groups">
                      <option value="Pre-Approval">Pre-Approval</option>
                      <option value="Approved">Approved</option>
                    </optgroup>
                    <optgroup label="Individual Statuses">
                      <option value="1">InUploadProcess</option>
                      <option value="2">InPMReview</option>
                      <option value="3">InSPMReview</option>
                      <option value="4">InDMReview</option>
                      <option value="5">InOpsReview</option>
                      <option value="6">OpsApproved</option>
                      <option value="7">Rejected</option>
                      <option value="8">Busted</option>
                      <option value="9">InPayrollProcess</option>
                      <option value="10">Paid</option>
                    </optgroup>
                  </select>
                </div>

                {/* Bonus Type Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Bonus Type
                  </label>
                  <select
                    value={filters.bonusTypeId}
                    onChange={(e) => handleFilterChange('bonusTypeId', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="1">Stage5 Bonus</option>
                    <option value="2">Closing Bonus</option>
                  </select>
                </div>

                {/* Date Filter Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date Filter
                  </label>
                  <select
                    value={filters.dateFilterType}
                    onChange={(e) => handleFilterChange('dateFilterType', e.target.value as DateFilterType)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="none">No Date Filter</option>
                    <option value="month">Specific Month</option>
                    <option value="dateRange">Date Range</option>
                    <option value="before">Before Date</option>
                    <option value="after">After Date</option>
                  </select>
                </div>

                {/* Date Input Fields */}
                {filters.dateFilterType === 'month' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Select Month
                    </label>
                    <input
                      type="month"
                      value={filters.month}
                      onChange={(e) => handleFilterChange('month', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                )}

                {filters.dateFilterType === 'dateRange' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Start Date
                      </label>
                      <input
                        type="date"
                        value={filters.startDate}
                        onChange={(e) => handleFilterChange('startDate', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        End Date
                      </label>
                      <input
                        type="date"
                        value={filters.endDate}
                        onChange={(e) => handleFilterChange('endDate', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                  </>
                )}

                {filters.dateFilterType === 'before' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Before Date
                    </label>
                    <input
                      type="date"
                      value={filters.beforeDate}
                      onChange={(e) => handleFilterChange('beforeDate', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                )}

                {filters.dateFilterType === 'after' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      After Date
                    </label>
                    <input
                      type="date"
                      value={filters.afterDate}
                      onChange={(e) => handleFilterChange('afterDate', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                )}
              </div>

              <div className="flex gap-2 mt-4">
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 text-sm text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
                >
                  Clear Filters
                </button>
                <span className="text-sm text-gray-600 self-center">
                  Showing {filteredRows.length} of {rows.length} jobs
                  
                </span>
              </div>
            </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-200">
              <tr>
                {columns.map((column, index) => (
                  <th 
                    key={index}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap"
                  >
                    {column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={columns.length || 7} className="px-6 py-4 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-900 mx-auto"></div>
                  </td>
                </tr>
              ) : filteredRows.length > 0 ? (
                filteredRows.map((row, rowIndex) => {
                  if (isApproved) {
                    // Approved jobs: one row per job, all CM data in columns
                    return (
                      <tr
                        key={`approved-${row.jobId}-${rowIndex}`}
                        className="hover:bg-gray-100"
                      >
                        {columns.map((column, colIndex) => {
                          const cellValue = getApprovedCellValue(row, column, bonusType);
                          // Format currency for bonus amounts and prices
                          let displayValue: string | number = cellValue;
                          
                          if (typeof cellValue === 'number') {
                            if (column.includes('Bonus Amount') || column.includes('Bonus') || column.includes('Price')) {
                              displayValue = cellValue > 0 ? formatCurrency(cellValue) : '';
                            } else if (column.includes('ID') || column.includes('Year') || column.includes('Month')) {
                              displayValue = cellValue || '';
                            } else {
                              displayValue = cellValue || '';
                            }
                          } else {
                            displayValue = cellValue || '';
                          }
                          
                          return (
                            <td key={colIndex} className="px-6 py-4 whitespace-nowrap text-sm">
                              {displayValue}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  } else {
                    // Pending jobs: one row per job
                    return (
                      <tr
                        key={`pending-${row.jobId}-${rowIndex}`}
                        className="hover:bg-gray-100"
                      >
                        {columns.map((column, colIndex) => {
                          const cellValue = getPendingCellValue(row, column, bonusType);
                          return (
                            <td key={colIndex} className="px-6 py-4 whitespace-nowrap text-sm">
                              {cellValue}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  }
                })
              ) : (
                <tr>
                  <td colSpan={columns.length || 7} className="px-6 py-4 text-center">
                    <div className="py-20">
                      <h6 className="text-xl font-semibold">
                        {rows.length > 0 ? 'No items match the current filters.' : 'No items in report.'}
                      </h6>
                      <p className="text-gray-500 mt-2">
                        {rows.length > 0 
                          ? 'Try adjusting your filters to see more results.' 
                          : 'Please approve more projects before returning.'}
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </main>
  );
}

